import { Box, Chip, LinearProgress, Tooltip, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

// TODO: (nachiket) When migrating to use shared libs instead of forked ones, replace these usages with the correct VDS tokens.
import { colors } from '@verantos-dev/shared-ui-components/tokens';

import { StatusDot } from './StatusIcon';
import { useAccuracyJobCell } from './useAccuracyJobCell';
import type { AccuracyJobSummaryRow } from '../../generate-accuracy/types/response';
import { formatEtaSeconds } from '../emitters/formatAccuracyJobCell';

interface AccuracyJobCellProps {
  job: AccuracyJobSummaryRow | undefined;
  onOpenRunHistory?: () => void;
}

/**
 * Purely presentational — no query hooks, no polling. `job` is looked up by the
 * caller from `useAccuracyJobColumn`'s `jobsByProjectId` map and passed in through
 * the column definition's closure. See the design doc's run-state matrix.
 *
 * Clicking any populated cell calls `onOpenRunHistory`, which opens the Accuracy
 * runs modal for the row's project. The empty (`no-run`) cell is not clickable —
 * there is no history to show.
 */
// TODO: cleanup with cp_trigger_accuracy_from_ui ff
export const AccuracyJobCell = ({ job, onOpenRunHistory }: AccuracyJobCellProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { status, label, tooltip, percent, etaSeconds, secondaryParts, errorMessage } =
    useAccuracyJobCell(job);

  if (status === 'no-run') {
    return <Typography variant="body2">{label}</Typography>;
  }

  if (status === 'pending') {
    return (
      <Tooltip title={tooltip}>
        <Box sx={{ cursor: 'pointer' }} onClick={onOpenRunHistory}>
          <Typography variant="body2">{label}</Typography>
        </Box>
      </Tooltip>
    );
  }

  if (status === 'in-progress') {
    return (
      <Tooltip title={tooltip}>
        <Box
          onClick={onOpenRunHistory}
          sx={{
            cursor: 'pointer',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ display: 'flex', gap: theme.spacing(4) }}>
            <Typography variant="body2">{label}</Typography>
            <Typography variant="body2">{percent}%</Typography>
          </Box>

          <LinearProgress
            variant="determinate"
            value={percent ?? 0}
            sx={{
              my: theme.spacing(1),
              height: 6,
              borderRadius: 3,
              width: '100%',
            }}
          />

          {etaSeconds !== null && etaSeconds !== undefined && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                lineHeight: 1,
                mt: 0.25,
              }}
            >
              {formatEtaSeconds(etaSeconds, formatMessage)}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  }

  if (status === 'completed') {
    return (
      <Tooltip title={tooltip}>
        <Box
          onClick={onOpenRunHistory}
          sx={{
            cursor: 'pointer',
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: theme.spacing(2),
          }}
        >
          <Chip
            icon={<StatusDot color={theme.palette.success.main} />}
            label={label}
            size="small"
            sx={{
              width: 'fit-content',
              px: theme.spacing(3),
              backgroundColor: colors['success-50'],
              color: colors['success-600'],
              '& .MuiChip-icon': {
                color: colors['success-600'],
                fontSize: 18,
              },
              fontSize: '10px',
              borderColor: colors['success-600'],
            }}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'block',
              lineHeight: 1.2,
              mt: 0.25,
            }}
          >
            {secondaryParts?.join(' · ')}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip}>
      <Box
        onClick={onOpenRunHistory}
        sx={{
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: theme.spacing(2),
        }}
      >
        <Chip
          icon={<StatusDot color={theme.palette.error.main} />}
          label={label}
          size="small"
          sx={{
            width: 'fit-content',
            px: theme.spacing(3),
            backgroundColor: colors['error-50'],
            color: colors['error-600'],
            '& .MuiChip-icon': {
              color: colors['error-600'],
              fontSize: 18,
            },
            fontSize: '10px',
            borderColor: colors['error-600'],
          }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{
            display: 'block',
            lineHeight: 1.2,
            mt: 0.25,
          }}
        >
          {errorMessage}
        </Typography>
      </Box>
    </Tooltip>
  );
};
